'use client';

import { useAudio } from '@/components/providers/AudioProvider';

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function PersistentPlayer() {
  const {
    currentEpisode,
    playing,
    currentTime,
    duration,
    volume,
    togglePlay,
    seek,
    setVolume,
    playNext,
    playPrev,
    playlist,
    playlistIndex,
  } = useAudio();

  if (!currentEpisode) return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="persistent-player">
      <div className="persistent-player-inner">
        <div className="persistent-player-info">
          <span className="persistent-player-badge">THE MARQUEE POD</span>
          <span className="persistent-player-title">{currentEpisode.title}</span>
        </div>

        <div className="persistent-player-controls">
          <button
            className="persistent-player-btn"
            onClick={playPrev}
            disabled={playlistIndex <= 0}
            aria-label="Previous episode"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
            </svg>
          </button>

          <button
            className="persistent-player-btn persistent-player-btn-play"
            onClick={togglePlay}
            aria-label={playing ? 'Pause' : 'Play'}
          >
            {playing ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 4h4v16H6zm8 0h4v16h-4z" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          <button
            className="persistent-player-btn"
            onClick={playNext}
            disabled={playlistIndex >= playlist.length - 1}
            aria-label="Next episode"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
            </svg>
          </button>
        </div>

        <div className="persistent-player-progress">
          <span className="persistent-player-time">{formatTime(currentTime)}</span>
          <div
            className="persistent-player-bar"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const pct = (e.clientX - rect.left) / rect.width;
              seek(pct * duration);
            }}
          >
            <div
              className="persistent-player-bar-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="persistent-player-time">{formatTime(duration)}</span>
        </div>

        <div className="persistent-player-volume">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0014 8.14v7.72A4.49 4.49 0 0016.5 12z" />
          </svg>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="persistent-player-volume-slider"
            aria-label="Volume"
          />
        </div>
      </div>
    </div>
  );
}
